import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, StatusBadge, Modal, ConfirmModal, Ico, Icons } from '../../components/common';
import AdminNav from '../../components/admin/AdminNav';

const emptyForm = { name: '', type: 'Knockout', status: 'upcoming', surface: 'Hard Court', startDate: '', endDate: '', location: '', prize: '', maxPlayers: 8, description: '', registeredPlayers: [] };

export default function AdminTournaments() {
  const { tournaments, players, addTournament, updateTournament, deleteTournament, fetchTournaments, fetchPlayers } = useApp();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    if (tournaments.length === 0) fetchTournaments();
    if (players.length === 0) fetchPlayers();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setModal(true); };
  const openEdit = (t) => { setForm(t); setEditId(t.id); setModal(true); };

  const handleSave = (e) => {
    e.preventDefault();
    if (editId) updateTournament(editId, form);
    else addTournament(form);
    setModal(false);
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const filtered = tournaments.filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || t.location.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={styles.container}>
      <AdminNav />
      <div style={styles.header}>
        <h1 style={styles.title}>Tournaments</h1>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <input style={styles.search} placeholder="Search tournaments..." value={search} onChange={e => setSearch(e.target.value)} />
        <button onClick={openNew} style={styles.addBtn}>{isMobile ? '+ Add' : '+ New Tournament'}</button>
      </div>

      <div style={styles.grid}>
        {filtered.map(t => {
          const dateStr = t.startDate ? new Date(t.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-';
          const endStr = t.endDate ? new Date(t.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
          return (
          <Card key={t.id}>
            <div style={styles.cardHeader}>
              <h3 style={{ margin: 0 }}>{t.name}</h3>
              <StatusBadge status={t.status} />
            </div>
            <div style={styles.meta}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Ico d={Icons.pin} size={12} color="#666" /> {t.location}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Ico d={Icons.racket} size={12} color="#666" /> {t.surface}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Ico d={Icons.trophy} size={12} color="#666" /> {t.type}</span>
            </div>
            <div style={styles.meta}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Ico d={Icons.calendar} size={12} color="#666" /> {dateStr} — {endStr}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Ico d={Icons.trophy} size={12} color="#666" /> {t.prize}</span>
            </div>
            <div style={styles.meta}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Ico d={Icons.users} size={12} color="#666" /> {t.registeredPlayers.length}/{t.maxPlayers} players</span>
            </div>
            <div style={styles.playerChips}>
              {t.registeredPlayers.map(pid => {
                const p = players.find(pl => pl.id === pid);
                return p ? <span key={pid} style={styles.chip}>{p.name}</span> : null;
              })}
            </div>
            <div style={styles.actions}>
              <button onClick={() => openEdit(t)} style={styles.editBtn}>Edit</button>
              <button onClick={() => setDeleteConfirm(t)} style={styles.deleteBtn}>Delete</button>
            </div>
          </Card>
        );
        })}
      </div>

      <Modal isOpen={modal} onClose={() => setModal(false)} title={editId ? 'Edit Tournament' : 'New Tournament'}>
        <form onSubmit={handleSave}>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Name</label>
              <input style={styles.input} value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div>
              <label style={styles.label}>Type</label>
              <select style={styles.input} value={form.type} onChange={e => set('type', e.target.value)}>
                <option>Knockout</option>
                <option>Round Robin</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Status</label>
              <select style={styles.input} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Surface</label>
              <select style={styles.input} value={form.surface} onChange={e => set('surface', e.target.value)}>
                <option>Hard Court</option>
                <option>Clay</option>
                <option>Grass</option>
                <option>Indoor</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Start Date</label>
              <input style={styles.input} type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} required />
            </div>
            <div>
              <label style={styles.label}>End Date</label>
              <input style={styles.input} type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} required />
            </div>
            <div>
              <label style={styles.label}>Location</label>
              <input style={styles.input} value={form.location} onChange={e => set('location', e.target.value)} required />
            </div>
            <div>
              <label style={styles.label}>Prize</label>
              <input style={styles.input} value={form.prize} onChange={e => set('prize', e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Max Players</label>
              <select style={styles.input} value={form.maxPlayers} onChange={e => set('maxPlayers', parseInt(e.target.value))}>
                <option value={4}>4</option>
                <option value={8}>8</option>
                <option value={16}>16</option>
                <option value={32}>32</option>
              </select>
            </div>
          </div>
          <label style={styles.label}>Description</label>
          <textarea style={{ ...styles.input, minHeight: 60 }} value={form.description} onChange={e => set('description', e.target.value)} />
          <label style={styles.label}>Register Players</label>
          <div style={{ ...styles.playerChips, marginBottom: 16 }}>
            {players.map(p => (
              <label key={p.id} style={{ ...styles.chip, cursor: 'pointer', background: form.registeredPlayers?.includes(p.id) ? '#4ecca3' : '#eee', color: form.registeredPlayers?.includes(p.id) ? '#fff' : '#333' }}>
                <input type="checkbox" style={{ display: 'none' }} checked={form.registeredPlayers?.includes(p.id) || false}
                  onChange={e => {
                    const rp = form.registeredPlayers || [];
                    set('registeredPlayers', e.target.checked ? [...rp, p.id] : rp.filter(id => id !== p.id));
                  }} />
                {p.name}
              </label>
            ))}
          </div>
          <button type="submit" style={styles.addBtn}>{editId ? 'Update' : 'Create'} Tournament</button>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            deleteTournament(deleteConfirm.id);
            setDeleteConfirm(null);
          }
        }}
        title="Delete Tournament"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        icon="🗑️"
      />
    </div>
  );
}

const styles = {
  container: { minHeight: 'calc(100vh - 52px)', background: 'var(--bg)', padding: '16px' },
  header: { marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif", marginBottom: 4 },
  search: { padding: '8px 12px', border: '1px solid #EEF1F6', borderRadius: 8, fontSize: 13, outline: 'none', flex: 1, minWidth: 140, background: '#fff' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  meta: { display: 'flex', gap: 12, fontSize: 12, color: '#8892A6', marginBottom: 6, flexWrap: 'wrap' },
  playerChips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  chip: { padding: '4px 10px', borderRadius: 12, fontSize: 11, background: '#E8F5E9', color: '#2E7D32', fontWeight: 600 },
  actions: { display: 'flex', gap: 8, marginTop: 12, borderTop: '1px solid #EEF1F6', paddingTop: 12 },
  editBtn: { flex: 1, padding: '8px 16px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  deleteBtn: { flex: 1, padding: '8px 16px', background: '#E53935', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  addBtn: { padding: '8px 16px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  label: { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block', marginTop: 8 },
  input: { width: '100%', padding: 10, border: '1px solid #EEF1F6', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none', background: '#fff' },
};
